import { useCallback, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { Base64 } from 'js-base64';
import Lottie from 'react-lottie';

import { Box, Stack, xcss } from '@atlaskit/primitives';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import ProgressBar from '../../sections/progress-bar';
import ErrorView from '../error/error-view';

import { LoomOnboardingText } from './components/loom-onboarding-text';
import LoomProvisioningAnimation from './lotties/loom-provisioning-animation.json';
import { useOnboardingData } from './utilities/save-data';

import type { OnboardingData } from './utilities/types';

const buildLoomRedirectUrl = (
	isProduction: boolean,
	cloudId: string,
	onboardingData: OnboardingData,
): string => {
	const searchParams = new URLSearchParams(window.location.search);
	const loomSource = searchParams.get('source');

	const params = new URLSearchParams();

	// For signup attribution
	if (loomSource) {
		params.set('source', loomSource);
	}

	// Add onboarding data
	params.set('data', Base64.encode(JSON.stringify({ 'onboarding-data': onboardingData })));

	// Send off!
	if (isProduction) {
		params.set('redirect_after', `https://www.loom.com/workspace/cloud/${cloudId}`);
		return `https://loom.com/api/auth/atlassian?${params.toString()}`;
	}

	params.set('redirect_after', `https://stage.loom.com/workspace/cloud/${cloudId}`);
	return `https://stage.loom.com/api/auth/atlassian?${params.toString()}`;
};

type Screen = 'provisioning' | 'timeout' | 'error';

export const AfterOnboardingStep = () => {
	const { createCloudStatus, cloudId, isProduction } = useCloudStatus();
	const { clearOnboardingData } = useOnboardingData();
	const [screen, setScreen] = useState<Screen>('provisioning');
	const [hasRedirected, setHasRedirected] = useState(false);

	const sendToLoom = useCallback(async () => {
		// Pull the final onboarding snapshot out of localStorage and clear it
		// so a subsequent signup on the same browser starts fresh.
		const onboardingData = clearOnboardingData();
		const url = buildLoomRedirectUrl(isProduction, cloudId, onboardingData);
		window.location.assign(url);
	}, [clearOnboardingData, cloudId, isProduction]);

	useEffect(() => {
		if (hasRedirected) {
			return;
		}

		switch (createCloudStatus) {
			case 'success':
				// Provisioning finished — hand off to Loom.
				setHasRedirected(true);
				void sendToLoom();
				return;
			case 'timeout':
			case 'timeout-recoverable':
				setScreen('timeout');
				return;
			case 'error':
			case 'error-unrecoverable':
				setScreen('error');
				return;
			case 'pending':
			default:
				setScreen('provisioning');
				return;
		}
	}, [createCloudStatus, hasRedirected, sendToLoom]);

	if (screen === 'error') {
		return <ErrorView bundle="loom" />;
	}

	const isTimeout = screen === 'timeout';

	return (
		<Box
			testId={isTimeout ? 'after-onboarding-step-timeout' : 'after-onboarding-step-provisioning'}
			xcss={centeredBoxStyles}
		>
			<div css={[contentRowStyles, contentWidthStyles]}>
				<Stack space="space.150" alignInline="center">
					<div css={lottieContainerStyles} aria-hidden="true">
						<Lottie
							options={{
								loop: true,
								autoplay: true,
								animationData: LoomProvisioningAnimation,
							}}
						/>
					</div>
					<div css={progressBarWrapperStyles}>
						<ProgressBar suppressTimeoutState />
					</div>
					<LoomOnboardingText align="center">
						{isTimeout
							? 'This is taking a bit longer than expected. Keep this tab open so we can get you started right away.'
							: "Getting your workspace ready. Keep this tab open, this shouldn't take more than a minute."}
					</LoomOnboardingText>
				</Stack>
			</div>
		</Box>
	);
};

// Fill the remaining viewport and vertically center the content, while guaranteeing
// a minimum 144px gap between the top of the screen and the Lottie.
const centeredBoxStyles = xcss({
	display: 'grid',
	gridTemplateRows: 'minmax(144px, 1fr) auto 1fr',
	justifyItems: 'center',
	width: '100%',
	minHeight: 'calc(100vh - 8rem)',
});

// Place the content in the middle grid track so the top `minmax(144px, 1fr)` and
// bottom `1fr` tracks flank it.
const contentRowStyles = css({
	gridRow: 2,
});

// Constrain the whole content column to a single width so the Lottie
const contentWidthStyles = css({
	width: '100%',
	maxWidth: '600px',
});

const lottieContainerStyles = css({
	width: '100%',
});

const progressBarWrapperStyles = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
});
